import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getRecentColors, addRecentColor, normalizeColor } from '../../utils/recentColors';
import './ColorPicker.css';

// Reusable colour picker used everywhere: a small swatch button that opens a
// popover with a dense colour grid (organised by hue → shade, like Office/
// Google), the shared per-user RECENT colours, and a full custom picker.
// Drop-in: <ColorPicker value onChange />.
//
// Props:
//   value     — current colour (#rrggbb)
//   onChange  — (color) => void; also records the colour as "recent"
//   title     — tooltip / aria-label for the swatch button
//   size      — swatch button size in px (default 16)
//   align     — popover horizontal align: 'left' | 'right' (default 'left')

// One hue per column; shades run light → dark down the rows. Plus a neutral
// (greyscale) top row. Gives a professional, organised grid.
const HUES = ['#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c', '#3498db', '#9b59b6', '#e84393'];
const NEUTRALS = ['#000000', '#3a3a3a', '#5c5c5c', '#858585', '#adadad', '#d6d6d6', '#f0f0f0', '#ffffff'];
const SHADE_LEVELS = [0.66, 0.4, 0.18, 0, -0.25, -0.5]; // + = lighter (mix white), − = darker
const GRID_COLS = HUES.length; // 8

function shade(hex, pct) {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  if (pct > 0) { r += (255 - r) * pct; g += (255 - g) * pct; b += (255 - b) * pct; }
  else if (pct < 0) { const f = 1 + pct; r *= f; g *= f; b *= f; }
  const to = (x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Flat, row-major grid: neutrals row first, then each shade level across hues.
// (Kept for a caller that passes its own `presets`; the default is THEME.)
const DEFAULT_GRID = [
  ...NEUTRALS,
  ...SHADE_LEVELS.flatMap(L => HUES.map(h => shade(h, L))),
];

// ── THE PALETTE (2026-10-07, direction B of the mock-ups) ─────────────────
// Ten THEME colours, each with three tints and two shades beneath it, then
// a row of STANDARD colours: the set the user picked from the mock-ups.
const THEME_COLOURS = [
  ['#000000', 'Black'], ['#FFFFFF', 'White'], ['#44546A', 'Blue grey'], ['#E7E6E6', 'Paper'],
  ['#4472C4', 'Blue'], ['#ED7D31', 'Orange'], ['#A5A5A5', 'Grey'], ['#FFC000', 'Gold'],
  ['#5B9BD5', 'Sky'], ['#70AD47', 'Green'],
];
const STANDARD_COLOURS = [
  ['#C00000', 'Dark red'], ['#FF0000', 'Red'], ['#FFC000', 'Gold'], ['#FFFF00', 'Yellow'],
  ['#92D050', 'Light green'], ['#00B050', 'Green'], ['#00B0F0', 'Light blue'], ['#0070C0', 'Blue'],
  ['#002060', 'Navy'], ['#7030A0', 'Purple'],
];
// Lighter 80 / 60 / 40 %, darker 25 / 50 %. The black column runs to greys
// and the white column to light greys, or they would be five blacks and
// five whites.
const TINT_STEPS = [
  { t: 0.8, label: 'lighter 80%' }, { t: 0.6, label: 'lighter 60%' }, { t: 0.4, label: 'lighter 40%' },
  { t: -0.25, label: 'darker 25%' }, { t: -0.5, label: 'darker 50%' },
];
const tintOf = (hex, t) => {
  if (hex === '#000000') return shade(hex, t > 0 ? t * 0.85 : Math.max(0.05, 0.6 + t));
  if (hex === '#FFFFFF') return shade(hex, t > 0 ? -(1 - t) * 0.25 : t * 0.7);
  return shade(hex, t);
};
const THEME_ROWS = TINT_STEPS.map(({ t, label }) => THEME_COLOURS.map(([hex, name]) => [tintOf(hex, t), `${name}, ${label}`]));

export default function ColorPicker({
  value, onChange, title = 'Colour', size = 18, align = 'left',
  presets = DEFAULT_GRID, disabled = false, className = '', style,
  /**
   * Offer "no colour" as the last cell of the grid — bottom-right, drawn as a
   * swatch with a red line through it, exactly the way every drawing package
   * shows it.
   *
   * It belongs IN the palette rather than beside it as a separate None button:
   * no-fill is a choice about colour, so it lives with the other choices, and
   * a button that sits outside is one more thing on a crowded bar.
   */
  allowNone = false,
  /**
   * More about the FILL than its colour, at the foot of the pop-up (a
   * shape's hatch and its Highlight switch, 2026-10-07): they used to be two
   * more controls on every row. A node, or a function of { close }.
   */
  footer = null,
}) {
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState(getRecentColors);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const POP_W = 248;
  // The CUSTOM picker reports every colour passed on the way while it is
  // dragged. Each is applied live, but only the one it ends on becomes a
  // recent colour: on the picker's own 'change' (it closed) or when this
  // pop-up closes (2026-10-07: a drag used to fill Recent with every step).
  const customRef = useRef(null);
  const pendingCustom = useRef(null);
  const commitCustom = useCallback(() => {
    const c = pendingCustom.current;
    pendingCustom.current = null;
    if (c) setRecents(addRecentColor(c));
  }, []);
  useEffect(() => {
    const el = customRef.current;
    if (!el) return undefined;
    el.addEventListener('change', commitCustom);
    return () => el.removeEventListener('change', commitCustom);
  });
  useEffect(() => { if (!open) commitCustom(); }, [open, commitCustom]);

  useEffect(() => {
    const refresh = () => setRecents(getRecentColors());
    window.addEventListener('pidly:recent-colors-updated', refresh);
    return () => window.removeEventListener('pidly:recent-colors-updated', refresh);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.color-picker-pop')) return;
      // A menu opened FROM the footer (the hatch list) is part of this pop-up.
      if (e.target.closest?.('.hp-menu, [data-color-pop-child]')) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const openPop = () => {
    if (disabled) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const left = align === 'right' ? Math.max(8, r.right - POP_W) : Math.min(r.left, window.innerWidth - POP_W - 8);
      setPos({ top: r.bottom + 6, left });
    }
    setRecents(getRecentColors());
    setOpen(o => !o);
  };

  // A PICK NEVER CLOSES THE PALETTE (2026-10-28, by the user: every menu on
  // the options row stays up until a click lands outside it, Escape, or its
  // own trigger). Picking is trying: one swatch, then the next, watching the
  // markup change under the open palette.
  const pick = useCallback((c) => {
    const n = normalizeColor(c) || c;
    onChange?.(n);
    setRecents(addRecentColor(n));
  }, [onChange]);

  // 'none' is a legitimate value, not a colour to be normalised — running it
  // through normalizeColor would turn it into black and the swatch would claim
  // the shape is filled.
  const isNone = value === 'none';
  const current = isNone ? 'none' : (normalizeColor(value) || value || '#000000');

  return (
    <>
      {/* Visual = a real <input type="color"> so it inherits the exact same
          styling as the other native colour inputs around it (e.g. the
          highlighter/arrow swatches). pointer-events are off on the input; the
          wrapper handles the click and opens our popover instead of the native
          picker. */}
      <span
        ref={btnRef}
        className={`color-picker-trigger${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
        onClick={openPop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        title={title}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPop(); } }}
      >
        {/* A plain <span>, not <input type="color">.
            The input was only ever there to show the colour — the wrapper owns
            the click and the popover does the picking — but its rendering is
            the browser's, not ours. Chrome draws it as a wrapper with padding
            around an inner swatch that carries its own border and radius, so
            the visible block stayed rounded and inset however the box was
            styled, and as an inline element it overflowed a line-height:0
            parent, leaving parts of it outside the element that handles the
            click. Three rounds of CSS did not settle it. A span has no user-
            agent appearance to fight: it is exactly the size and shape it is
            told to be, and all of it is clickable. */}
        <span
          className={`color-picker-swatch${isNone ? ' color-picker-swatch--none' : ''}`}
          style={{
            ...(isNone ? null : { background: current }),
            ...(size && size !== 18 ? { width: size, height: size } : null),
            ...style,
          }}
          aria-hidden="true"
        />
      </span>
      {open && pos && createPortal(
        <div className="color-picker-pop" style={{ top: pos.top, left: pos.left, width: POP_W }} role="dialog" aria-label={title}>
          {presets === DEFAULT_GRID ? (
            <>
              {allowNone && (
                <button
                  type="button"
                  className={`color-picker-none-row${isNone ? ' is-active' : ''}`}
                  onClick={() => onChange?.('none')}
                >
                  <span className="color-picker-cell color-picker-cell--none" aria-hidden="true" />
                  No colour
                </button>
              )}
              <div className="color-picker-section-label">Theme Colours</div>
              <div className="color-picker-grid" style={{ gridTemplateColumns: 'repeat(10, 1fr)', marginBottom: 4 }}>
                {THEME_COLOURS.map(([c, name]) => (
                  <button key={c} type="button" className={`color-picker-cell${normalizeColor(c) === current ? ' is-active' : ''}`}
                    style={{ background: c }} onClick={() => pick(c)} title={`${name} ${c}`} aria-label={name} />
                ))}
              </div>
              <div className="color-picker-grid color-picker-grid--tints" style={{ gridTemplateColumns: 'repeat(10, 1fr)' }}>
                {THEME_ROWS.flat().map(([c, name], i) => (
                  <button key={`${c}-${i}`} type="button" className={`color-picker-cell${normalizeColor(c) === current ? ' is-active' : ''}`}
                    style={{ background: c }} onClick={() => pick(c)} title={`${name} ${c}`} aria-label={name} />
                ))}
              </div>
              <div className="color-picker-section-label">Standard Colours</div>
              <div className="color-picker-grid" style={{ gridTemplateColumns: 'repeat(10, 1fr)' }}>
                {STANDARD_COLOURS.map(([c, name]) => (
                  <button key={c} type="button" className={`color-picker-cell${normalizeColor(c) === current ? ' is-active' : ''}`}
                    style={{ background: c }} onClick={() => pick(c)} title={`${name} ${c}`} aria-label={name} />
                ))}
              </div>
            </>
          ) : (
          <div className="color-picker-grid" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}>
            {presets.map((c, i) => (
              <button
                key={`${c}-${i}`}
                type="button"
                className={`color-picker-cell${(normalizeColor(c) === current) ? ' is-active' : ''}`}
                style={{ background: c }}
                onClick={() => pick(c)}
                title={c}
              />
            ))}
            {allowNone && (
              // Last, so it lands in the bottom-right of the grid. Not routed
              // through pick(): that records a RECENT colour, and "none" is
              // not a colour anyone wants offered back to them as one.
              <button
                type="button"
                className={`color-picker-cell color-picker-cell--none${isNone ? ' is-active' : ''}`}
                onClick={() => onChange?.('none')}
                title="No colour"
                aria-label="No colour"
              />
            )}
          </div>
          )}

          <div className="color-picker-section-label">Recent</div>
          {recents.length === 0 ? (
            <div className="color-picker-empty">No recent colours yet</div>
          ) : (
            <div className="color-picker-grid" style={{ gridTemplateColumns: `repeat(${presets === DEFAULT_GRID ? 10 : GRID_COLS}, 1fr)` }}>
              {recents.map((c, i) => (
                <button
                  key={`${c}-${i}`}
                  type="button"
                  className={`color-picker-cell${(c === current) ? ' is-active' : ''}`}
                  style={{ background: c }}
                  onClick={() => pick(c)}
                  title={c}
                />
              ))}
            </div>
          )}

          <label className="color-picker-custom">
            <span className="color-picker-custom-chip" style={{ background: current }} />
            <span className="color-picker-custom-label">More Colours…</span>
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(current) ? current : '#000000'}
              ref={customRef}
              onChange={(e) => {
                const n = normalizeColor(e.target.value) || e.target.value;
                pendingCustom.current = n;
                onChange?.(n);
              }}
            />
          </label>
          {footer && (
            <div className="color-picker-footer">
              {typeof footer === 'function' ? footer({ close: () => setOpen(false) }) : footer}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
